import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { useAllCompanies, CompanyOption } from '../hooks/useAllCompanies';

// One-time: move ALL existing taka records (and taka groups) to a chosen company.
// New entries keep the entering company; this is just for the current data.
const TakaCompanyAssignPanel: React.FC = () => {
  const qc = useQueryClient();
  const { data } = useAllCompanies();
  const companies: CompanyOption[] = data || [];
  const [tenantId, setTenantId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    if (tenantId === '') { toast.error('Pick a company first'); return; }
    const name = companies.find((c: CompanyOption) => c.tenantId === tenantId)?.name || 'this company';
    if (!window.confirm(`Move ALL existing Taka Inventory (and taka groups) to ${name}?`)) return;
    setBusy(true);
    try {
      const res: any = await apiService.post('/taka-company/assign-all', { tenantId: Number(tenantId) });
      toast.success(`Moved ${res.movedTaka} taka, ${res.movedGroups} group(s)`);
      qc.invalidateQueries();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to assign');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E1E6EF] p-4 sm:p-5">
      <h3 className="text-base font-bold text-[#0E131B]">Taka Inventory → Company</h3>
      <p className="text-xs text-slate-500 mb-3">Move all existing taka records to one company (one-time). New entries keep the entering company.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value === '' ? '' : Number(e.target.value))}
          className="flex-1 min-w-[10rem] rounded-lg border border-[#E8EBF1] bg-[#F7F8FB] px-2.5 py-2 text-sm"
        >
          <option value="">Select company…</option>
          {companies.map((c: CompanyOption) => (
            <option key={c.tenantId} value={c.tenantId}>{c.name}</option>
          ))}
        </select>
        <button
          onClick={assign}
          disabled={busy || tenantId === ''}
          className="flex-shrink-0 px-3 py-2 rounded-lg bg-[#0055a9] text-white text-sm font-semibold disabled:opacity-60"
        >
          {busy ? 'Assigning…' : 'Assign all existing taka'}
        </button>
      </div>
    </div>
  );
};

export default TakaCompanyAssignPanel;
